import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnChanges, OnInit, Optional, Self, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { NgbDatepicker, NgbDatepickerModule, NgbDateStruct, NgbTimepickerModule, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import { UserstorageService } from 'src/app/_theme/core/users/userstorage/userstorage.service';

@Component({
    selector: 'app-date-time-picker',
    templateUrl: './date-time-picker.component.html',
    styleUrls: [
        './date-time-picker.component.scss',
        './date-time-picker.component.mobile-dark.scss'
    ],
    standalone: true,
    imports: [
        FormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        NgbDatepickerModule,
        NgbTimepickerModule
    ]
})
export class DateTimePickerComponent implements ControlValueAccessor, OnInit, OnChanges {
    @Input() label: string = '';
    @Input() errorMsg: string = '';
    @Input() required: boolean = false;
    @Input() parentErrorKey?: string;
    @Input() is12hrsFormat: boolean = true;
    @Input() minDate?: NgbDateStruct;
    @Input() maxDate?: NgbDateStruct;
    @Input() minDateTime?: Date | null;
    @Input() timeZoneLabel?: string;

    isDisabled: boolean = false;
    showPicker = false;
    date: NgbDateStruct | null = null;
    time: NgbTimeStruct | null = null;
    draftDate: NgbDateStruct | null = null;
    draftTime: NgbTimeStruct | null = null;
    private minDateInternal: NgbDateStruct | null = null;
    locale: string = 'en-US';
    pickerPlacement: 'above' | 'below' = 'below';
    private readonly pickerInstanceId = Math.random().toString(36).slice(2);

    @ViewChild('pickerAnchor', { static: false }) pickerAnchor?: ElementRef<HTMLElement>;
    @ViewChild('pickerWrapper', { static: false }) pickerWrapper?: ElementRef<HTMLElement>;
    @ViewChild('pickerPanel', { static: false }) pickerPanel?: ElementRef<HTMLElement>;
    @ViewChild('datePicker') datePicker?: NgbDatepicker;

    onChange: (value: Date | null) => void = () => { };
    onTouched: () => void = () => { };

    constructor(
        @Optional() @Self() public ngControl: NgControl,
        private readonly userStorageService: UserstorageService,
        private readonly cd: ChangeDetectorRef
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
        dayjs.extend(localeData);
    }

    ngOnInit(): void {
        try {
            const stored = this.userStorageService.getuserstorage();
            this.locale = stored ? JSON.parse(stored)?.locale || 'en-US' : 'en-US';
        } catch (error) {
            // Fallback keeps the picker usable even if malformed profile data is stored.
            console.warn('Failed to read user locale from storage. Falling back to en-US.', error);
            this.locale = 'en-US';
        }
        dayjs.locale(this.locale);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['minDateTime'] || changes['maxDate']) {
            this.syncMinDateTime();
            this.enforceDraftBounds();
        }
    }

    writeValue(value: unknown): void {
        if (!value) {
            this.date = null;
            this.time = null;
            this.resetDraftFromCommitted();
            return;
        }

        const parsed = this.parseDate(value);
        if (!parsed) {
            this.date = null;
            this.time = null;
            this.resetDraftFromCommitted();
            return;
        }

        this.date = this.toNgbDateStruct(parsed);
        this.time = this.toNgbTimeStruct(parsed);
        this.resetDraftFromCommitted();
    }

    registerOnChange(fn: (value: Date | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    onBlur(): void {
        this.onTouched();
    }

    get showErrors(): boolean {
        if (!this.ngControl) return false;
        const ownError = this.ngControl.invalid && (this.ngControl.dirty || this.ngControl.touched);
        const parent = this.ngControl?.control?.parent;
        const parentError = this.parentErrorKey
            ? parent?.hasError(this.parentErrorKey) && (this.ngControl.dirty || this.ngControl.touched)
            : false;
        return !!(ownError || parentError);
    }

    get displayValue(): string {
        if (!this.date || !this.time) return '';
        return this.formatDateTime(this.date, this.time);
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.showPicker) return;
        const target = event.target as HTMLElement;
        const clickedInside = this.pickerWrapper?.nativeElement.contains(target) ?? false;
        if (!clickedInside) {
            this.cancelPicker(false);
        }
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscapeKey(event: KeyboardEvent): void {
        if (!this.showPicker) return;
        event.preventDefault();
        this.cancelPicker(false);
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.repositionPicker();
    }

    @HostListener('window:scroll')
    onWindowScroll(): void {
        this.repositionPicker();
    }

    @HostListener('window:date-time-picker-open', ['$event'])
    onAnotherPickerOpened(event: Event): void {
        const openedPickerId = (event as CustomEvent<string>).detail;
        if (openedPickerId !== this.pickerInstanceId && this.showPicker) {
            this.cancelPicker(false);
        }
    }

    openPicker(event: Event): void {
        if (this.isDisabled) return;
        event.stopPropagation();
        if (this.showPicker) return;

        if (globalThis.window) {
            globalThis.window.dispatchEvent(
                new CustomEvent<string>('date-time-picker-open', { detail: this.pickerInstanceId })
            );
        }

        this.syncMinDateTime();
        this.initializeDraftState();
        this.showPicker = true;
        this.cd.detectChanges();

        setTimeout(() => {
            this.updatePickerPlacement();
            this.cd.detectChanges();
            this.navigateToActiveMonth();
            this.pickerPanel?.nativeElement.focus();
        }, 0);
    }

    onTriggerKeydown(event: KeyboardEvent): void {
        const key = event.key;
        if (key === 'Enter' || key === ' ' || key === 'ArrowDown') {
            event.preventDefault();
            this.openPicker(event);
        }
    }

    onDateChange(date: NgbDateStruct): void {
        this.draftDate = date;
        this.enforceDraftBounds();
        this.commitDraftValue();
    }

    onTimeChange(time: NgbTimeStruct): void {
        this.draftTime = time;
        this.enforceDraftBounds();
        this.commitDraftValue();
    }

    onBackdropClick(event: MouseEvent): void {
        event.stopPropagation();
        this.cancelPicker(false);
    }

    onOkClick(): void {
        this.onTouched();
        this.closePicker();
    }

    cancelPicker(markTouched: boolean = true): void {
        if (markTouched) {
            this.onTouched();
        }
        this.resetDraftFromCommitted();
        this.closePicker();
    }

    get resolvedMinDate(): NgbDateStruct | undefined {
        return this.minDate ?? (this.minDateInternal || undefined);
    }

    private syncMinDateTime(): void {
        if (!this.minDateTime || Number.isNaN(this.minDateTime.getTime())) {
            this.minDateInternal = null;
            return;
        }
        this.minDateInternal = this.toNgbDateStruct(this.minDateTime);
    }

    private enforceDraftBounds(): void {
        const current = this.toDateObject(this.draftDate, this.draftTime);
        if (!current) return;
        this.setDraftFromDate(this.clampToBounds(current));
    }

    private clampToBounds(candidate: Date): Date {
        let bounded = new Date(candidate);

        if (this.maxDate) {
            const maxDateValue = new Date(
                this.maxDate.year,
                this.maxDate.month - 1,
                this.maxDate.day,
                23,
                59,
                59,
                999
            );
            if (bounded > maxDateValue) {
                bounded = maxDateValue;
            }
        }

        if (this.minDateTime && !Number.isNaN(this.minDateTime.getTime()) && bounded < this.minDateTime) {
            bounded = new Date(this.minDateTime);
        }

        return bounded;
    }

    private initializeDraftState(): void {
        this.resetDraftFromCommitted();

        if (!this.draftDate || !this.draftTime) {
            this.setDraftFromDate(this.getNowReferenceDate());
        }

        this.enforceDraftBounds();
    }

    private getNowReferenceDate(): Date {
        if (this.minDateTime && !Number.isNaN(this.minDateTime.getTime())) {
            return new Date(this.minDateTime);
        }
        return new Date();
    }

    private resetDraftFromCommitted(): void {
        this.draftDate = this.date ? { ...this.date } : null;
        this.draftTime = this.time ? { ...this.time } : null;
    }

    private closePicker(): void {
        this.showPicker = false;
        this.pickerPlacement = 'below';
        this.cd.detectChanges();
    }

    private repositionPicker(): void {
        if (!this.showPicker) return;
        this.updatePickerPlacement();
        this.cd.detectChanges();
    }

    private updatePickerPlacement(): void {
        if (globalThis.window === undefined) {
            this.pickerPlacement = 'below';
            return;
        }

        const browserWindow = globalThis.window;

        // Mobile uses a fixed bottom-sheet layout, so directional placement is desktop-only.
        if (browserWindow.matchMedia('(max-width: 767px)').matches) {
            this.pickerPlacement = 'below';
            return;
        }

        const anchorRect = this.pickerAnchor?.nativeElement.getBoundingClientRect();
        const panelElement = this.pickerPanel?.nativeElement;
        const panelHeight = panelElement?.offsetHeight;
        if (!anchorRect || !panelHeight || !panelElement) {
            this.pickerPlacement = 'below';
            return;
        }

        const viewportHeight = browserWindow.innerHeight || 0;
        const panelGap = 8;
        const spaceBelow = viewportHeight - anchorRect.bottom - panelGap;
        const spaceAbove = anchorRect.top - panelGap;
        this.pickerPlacement = (spaceBelow < panelHeight && spaceAbove > spaceBelow) ? 'above' : 'below';
    }

    private setDraftFromDate(date: Date): void {
        this.draftDate = this.toNgbDateStruct(date);
        this.draftTime = this.toNgbTimeStruct(date);
    }

    private navigateToActiveMonth(): void {
        if (!this.datePicker || !this.draftDate) return;
        this.datePicker.navigateTo({
            year: this.draftDate.year,
            month: this.draftDate.month
        });
    }

    private parseDate(value: unknown): Date | null {
        if (!value) return null;
        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value;
        }
        const date = new Date(value as string | number);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    private toNgbDateStruct(date: Date): NgbDateStruct {
        return {
            year: date.getFullYear(),
            month: date.getMonth() + 1,
            day: date.getDate()
        };
    }

    private toNgbTimeStruct(date: Date): NgbTimeStruct {
        return {
            hour: date.getHours(),
            minute: date.getMinutes(),
            second: date.getSeconds()
        };
    }

    private toDateObject(date?: NgbDateStruct | null, time?: NgbTimeStruct | null): Date | null {
        if (!date || !time) return null;
        return new Date(date.year, date.month - 1, date.day, time.hour, time.minute, time.second || 0);
    }

    private commitDraftValue(): void {
        const dateTime = this.toDateObject(this.draftDate, this.draftTime);

        if (dateTime && this.draftDate && this.draftTime) {
            this.date = { ...this.draftDate };
            this.time = { ...this.draftTime };
            this.onChange(dateTime);
            this.onTouched();
            return;
        }

        this.date = null;
        this.time = null;
        this.onChange(null);
        this.onTouched();
    }

    private formatDateTime(date: NgbDateStruct, time: NgbTimeStruct): string {
        if (!date || !time) return '';
        const jsDate = new Date(
            date.year,
            date.month - 1,
            date.day,
            time.hour,
            time.minute,
            time.second || 0
        );
        const format = this.is12hrsFormat ? 'L hh:mm A' : 'L HH:mm';
        return dayjs(jsDate).locale(this.locale).format(format);
    }
}
